
import { httpGet, httpPost } from '../lib/http';
import { endpoints, url } from '../api';
import { addBreadcrumb, captureAuthError } from '../lib/errorReporting';

// ========================================
// WEB DUAL AUTHENTICATION SERVICE
// Implements cookie-based auth with CSRF protection
// Per DUAL_AUTH_IMPLEMENTATION.md specification
// ========================================

const IS_DEV = process.env.NODE_ENV !== 'production';
const DEBUG_AUTH = IS_DEV && String(process.env.REACT_APP_DEBUG_AUTH) === 'true';
const dbg = (...args) => { if (DEBUG_AUTH) console.log('[authService]', ...args); };
const logWarn = (...args) => { if (IS_DEV) console.warn('[authService]', ...args); };

// CSRF Token Management (Web-Specific)
let csrfToken = null;

// Centralized logger for API calls and errors (never log tokens/passwords/PII)
function logApiCall(name, params) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    const safeParams = redact(params);
    console.info(`[API CALL] ${name}`, safeParams);
  }
}


// ========================================
// GET CSRF TOKEN (Web Required)
// ========================================
export async function getCsrfToken() {
  return safeApiCall(async () => {
    dbg('🔐 Fetching CSRF token from server...');
    let headersObj = null;
    const res = await fetch(url(endpoints.auth.csrf), {
      credentials: 'include', // Important: include cookies
    });

    // Debug: capture response headers for verification (only shown when DEBUG_AUTH)
    if (DEBUG_AUTH) {
      headersObj = {};
      try {
        for (const [k, v] of res.headers.entries()) headersObj[k] = v;
      } catch (e) {
        // ignore header parsing errors
      }
      console.log('[authService] CSRF response headers:', headersObj);
    }

    if (!res.ok) {
      throw new Error('Failed to get CSRF token');
    }

    const data = await res.json();
    csrfToken = data.csrf || data.csrfToken; // Support both formats
    
    // Debug logging (development only)
    if (DEBUG_AUTH) {
      console.log('[CSRF] Token received, length:', csrfToken?.length);
    }
    
    // Store CSRF token in sessionStorage for persistence
    sessionStorage.setItem('csrfToken', csrfToken);
    dbg('✅ CSRF token obtained and stored');
    
    // Return headers for debugging when enabled
    if (DEBUG_AUTH) {
      return { success: true, csrf: csrfToken, headers: headersObj };
    }

    return { success: true, csrf: csrfToken };
  }, 'getCsrfToken');
}

// Get stored CSRF token
export function getStoredCsrfToken() {
  if (!csrfToken) {
    csrfToken = sessionStorage.getItem('csrfToken');
  }
  return csrfToken;
}

// ========================================
// LOGOUT (Cookie-based)
// ========================================
export async function logout() {
  return safeApiCall(async () => {
    dbg('Logging out');
    
    // Call backend logout endpoint to clear cookies
    const path = endpoints.auth.logout;
    try {
      await fetch(url(path), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getStoredCsrfToken() || '',
        },
        credentials: 'include',
      });
    } catch (error) {
      logWarn('Failed to invalidate session on server:', error.message);
    }

    // Clear local storage (but preserve preferences)
    const keysToPreserve = ['theme', 'language'];
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && !keysToPreserve.includes(key)) {
        localStorage.removeItem(key);
      }
    }

    // Clear session storage
    sessionStorage.clear();
    csrfToken = null;

    // Dispatch event to notify other tabs
    window.dispatchEvent(new Event('auth:logout'));

    dbg('Logout complete');
    return { success: true };
  }, 'logout');
}


function logApiError(name, params, error) {
  if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    const safeParams = redact(params);
    console.error(`[API ERROR] ${name}`, safeParams, error);
  }
}
async function safeApiCall(fn, name, params) {
  logApiCall(name, params);
  try {
    return await fn();
  } catch (error) {
    logApiError(name, params, error);
    return { success: false, error: error?.message || 'Unknown error' };
  }
}


// Forgot password (calls backend endpoint)
export async function forgotPassword(email, continueUrl) {
  return safeApiCall(async () => {
    const path = (endpoints.auth && endpoints.auth.forgotPassword) || '/api/auth/forgot-password';
    const body = { email };
    if (continueUrl) {
      body.continueUrl = continueUrl;
    }
    const res = await httpPost(path, body);
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to send password reset email');
    }
    return { success: true };
  }, 'forgotPassword', { email });
}


// Verify password reset code (backend endpoint)
export async function verifyPasswordReset(oobCode) {
  return safeApiCall(async () => {
    if (!oobCode) throw new Error('Missing reset code');
    const path = (endpoints.auth && endpoints.auth.verifyPasswordReset) || '/api/auth/password-reset/verify';
    const res = await httpPost(path, { oobCode });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || 'Failed to verify password reset code');
    }
    return { success: true, ...data };
  }, 'verifyPasswordReset', { oobCode });
}


// Confirm password reset (backend endpoint)
export async function confirmPasswordReset(oobCode, newPassword) {
  return safeApiCall(async () => {
    if (!oobCode || !newPassword) throw new Error('Missing reset code or new password');
    const path = (endpoints.auth && endpoints.auth.confirmPasswordReset) || '/api/auth/password-reset/confirm';
    const res = await httpPost(path, { oobCode, newPassword });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || 'Failed to reset password');
    }
    return { success: true, ...data };
  }, 'confirmPasswordReset', { oobCode });
}


// Resend verification email (calls backend endpoint)
export async function resendVerificationEmail(email) {
  return safeApiCall(async () => {
    const path = (endpoints.auth && endpoints.auth.resendVerification) || '/api/auth/resend-verification';
    const res = await httpPost(path, { email });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to resend verification email');
    }
    return { success: true };
  }, 'resendVerificationEmail', { email });
}

// Sync email verification status to Firestore users collection (backend)
export async function syncEmailVerification(email) {
  return safeApiCall(async () => {
    if (!email) throw new Error('Missing email');
    const path = (endpoints.auth && endpoints.auth.syncEmailVerification) || '/api/auth/sync-email-verification';
    const res = await httpPost(path, { email });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || 'Failed to sync email verification');
    }
    return { success: true, ...data };
  }, 'syncEmailVerification', { email });
}


// Confirm email using backend endpoint (handles Firebase oobCode)
export async function confirmEmail(oobCode) {
  return safeApiCall(async () => {
    if (!oobCode) throw new Error('Missing confirmation code');
    const path = (endpoints.auth && endpoints.auth.confirmEmail) || '/api/auth/confirm-email';
    const res = await httpPost(path, { oobCode });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err?.message || 'Failed to confirm email');
    }
    return { success: true };
  }, 'confirmEmail', { oobCode });
}


// Verify email via backend verification endpoint (supports POST { token })
export async function verifyEmail(token) {
  return safeApiCall(async () => {
    if (!token) throw new Error('Missing verification token');
    const path = (endpoints.auth && endpoints.auth.verifyEmail) || '/api/auth/verify';
    const res = await httpPost(path, { token });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || 'Failed to verify email');
    }
    return { success: true, ...data };
  }, 'verifyEmail', { token });
}


// Confirm verification per frontend guide: POST { email } -> /api/auth/confirm-verification
export async function confirmVerification(email) {
  return safeApiCall(async () => {
    if (!email) throw new Error('Missing email for confirmation');
    const path = (endpoints.auth && endpoints.auth.confirmVerification) || '/api/auth/confirm-verification';
    const res = await httpPost(path, { email });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || 'Failed to confirm verification');
    }
    return { success: true, ...data };
  }, 'confirmVerification', { email });
}


// Input sanitization utility
function sanitizeInput(str) {
  if (typeof str !== 'string') return str;
  return str.replace(/[<>"'`]/g, '');
}

// Redact sensitive data for logging
const redact = (obj) => {
  try {
    if (!obj || typeof obj !== 'object') return obj;
    const copy = { ...obj };
    if ('password' in copy) copy.password = '***';
    if ('refreshToken' in copy) copy.refreshToken = '***';
    if ('accessToken' in copy) copy.accessToken = '***';
    if ('adminInviteCode' in copy) {
      const v = String(copy.adminInviteCode || '');
      copy.adminInviteCode = v ? `${v.slice(0, 3)}***` : '';
    }
    if ('email' in copy) copy.email = copy.email.replace(/(.{2}).+(@.*)/, '$1***$2');
    return copy;
  } catch {
    return obj;
  }
};

// ========================================
// LOGIN (Cookie-based with CSRF)
// ========================================
export async function loginWithPassword({ email, password }) {
  // Input validation
  if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
    return { success: false, error: 'Invalid email address.' };
  }
  if (!password || password.length < 6) {
    return { success: false, error: 'Password must be at least 6 characters.' };
  }
  
  return safeApiCall(async () => {
    // Ensure we have CSRF token before login
    // This is a fallback - normally CSRF is fetched on app init
    if (!csrfToken) {
      dbg('⚠️ CSRF token missing during login, fetching now...');
      await getCsrfToken();
    } else {
      dbg('✅ Using existing CSRF token for login');
    }
    
    dbg('🔑 Attempting login with password...');
    const path = endpoints.auth.login;

    // Sanitize inputs
    const payload = {
      email: sanitizeInput(email),
      password: sanitizeInput(password),
    };

    // Make request with CSRF token and credentials
    const res = await fetch(url(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      credentials: 'include', // Important: send/receive cookies
      body: JSON.stringify(payload),
    });

    // Try parse JSON payload (if any)
    const resData = await res.json().catch(() => null);
    if (!res.ok) {
      dbg('Login failed:', res.status, resData);
      // Return structured error so UI can act on codes like EMAIL_UNVERIFIED
      return {
        success: false,
        error: resData?.message || 'Login failed',
        code: resData?.code || null,
        emailVerified: typeof resData?.emailVerified === 'boolean' ? resData.emailVerified : null,
      };
    }

    const data = resData || {};
    
    // Clean up old mobile auth state if present
    if (localStorage.getItem('auth:state')) {
      localStorage.removeItem('auth:state');
    }
    
    // Update CSRF token if backend issued a new one after login
    const cookieMatch = document.cookie.match(/csrf=([^;]+)/);
    if (cookieMatch && cookieMatch[1] !== csrfToken) {
      csrfToken = cookieMatch[1];
      sessionStorage.setItem('csrfToken', csrfToken);
      dbg('Updated CSRF token from cookie');
    }
    
    // Web response: may include tokens for Bearer auth fallback
    // Store user info in localStorage for quick access
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }

    // If server provided an access token, keep it in-memory only (avoid persisting long-lived tokens)
    if (data.accessToken) {
      try {
        const { setAccessToken } = await import('./authClient');
        setAccessToken(data.accessToken, data.expiresIn);
      } catch (e) {}
    }
    // Do NOT store refreshToken in localStorage; server should set HttpOnly refresh cookie when appropriate
    
    // Notify AuthContext that user data has been updated
    try {
      window.dispatchEvent(new CustomEvent('auth:updated', { 
        detail: { source: 'login' } 
      }));
      dbg('✅ Auth updated event dispatched');
    } catch (e) {
      console.warn('Failed to dispatch auth:updated event:', e);
    }
    
    dbg('Login successful');
    return { ...data, success: true };
  }, 'loginWithPassword', { email });
}



// ========================================
// SIGNUP (Cookie-based with CSRF)
// ========================================
export async function signup(payload) {
  // Input validation
  if (!payload.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email)) {
    return { success: false, error: 'Invalid email address.' };
  }
  if (!payload.password || payload.password.length < 6) {
    return { success: false, error: 'Password must be at least 6 characters.' };
  }
  if (!payload.firstName || !payload.lastName) {
    return { success: false, error: 'First and last name are required.' };
  }
  
  return safeApiCall(async () => {
    // Ensure we have CSRF token before signup
    if (!csrfToken) {
      await getCsrfToken();
    }
    
    dbg('Signing up new user');
    
    // Sanitize all string fields
    const sanitized = {};
    for (const k in payload) {
      sanitized[k] = typeof payload[k] === 'string' ? sanitizeInput(payload[k]) : payload[k];
    }
    
    const path = endpoints.auth.signup;
    
    // Make request with CSRF token and credentials
    const res = await fetch(url(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      credentials: 'include', // Important: send/receive cookies
      body: JSON.stringify(sanitized),
    });
    
    if (!res.ok) {
      const err = await safeJson(res);
      throw new Error(err?.message || 'Signup failed');
    }
    
    const data = await res.json();
    
    // Web response: only user object (tokens in httpOnly cookies)
    // Store user info in localStorage for quick access
    if (data.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    if (data.accessToken) {
      try { const { setAccessToken } = await import('./authClient'); setAccessToken(data.accessToken, data.expiresIn); } catch (e) {}
    }
    // Do NOT persist refreshToken in localStorage
    
    dbg('Signup successful');
    return { ...data, user: data.user, success: true };
  }, 'signup', { email: payload.email });
}

async function safeJson(res) {
  try { return await res.json(); } catch { return null; }
}

// ========================================
// SESSION VALIDATION (Cookie-based)
// NOTE: This is NOT needed for cookie-based auth initialization
// The backend validates cookies automatically on every request
// This function can be used if you need to explicitly check session validity
// ========================================
export async function validateToken() {
  try {
    const path = endpoints.auth.validate || '/api/auth/validate-token';
    dbg('Validating session (explicit check)');
    
    const res = await fetch(url(path), {
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getStoredCsrfToken() || '',
      },
      credentials: 'include', // Important: send cookies
    });
    
    if (!res.ok) {
      const err = await safeJson(res);
      dbg('validate error', res.status, err);
      return { ok: false, status: res.status, error: err };
    }
    
    const data = await res.json();
    dbg('validate ok', { hasUser: Boolean(data?.user) });
    
    // Update local user info if available
    if (data?.user) {
      localStorage.setItem('user', JSON.stringify(data.user));
    }
    
    return { ok: true, status: res.status, user: data?.user || null };
  } catch (e) {
    dbg('validate exception', e?.message);
    return { ok: false, status: 0, error: e };
  }
}

// ========================================
// GET CURRENT USER (From localStorage)
// ========================================
export function getCurrentUser() {
  try {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  } catch {
    return null;
  }
}

// ========================================
// REFRESH SESSION (Web-Specific)
// ========================================
export async function refreshSession() {
  return safeApiCall(async () => {
    dbg('Refreshing session');

    const res = await fetch(url(endpoints.auth.refreshSession), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': getStoredCsrfToken() || '',
      },
      credentials: 'include',
    });

    if (!res.ok) {
      const err = await safeJson(res);
      captureAuthError(new Error(err?.message || 'Session refresh failed'), 'refresh');
      throw new Error(err?.message || 'Session refresh failed');
    }

    // Parse response and update tokens if provided
    try {
      const data = await res.json().catch(() => null);
      if (data?.accessToken) {
        try { const { setAccessToken } = await import('./authClient'); setAccessToken(data.accessToken, data.expiresIn); } catch (e) {}
      }
      // Do not persist refreshToken in localStorage
    } catch (e) {
      // ignore parse errors
    }

    dbg('Session refreshed');
    addBreadcrumb({ category: 'auth', message: 'Session refreshed', level: 'info' });
    return { success: true };
  }, 'refreshSession');
}

// Google sign-in helper: exchange idToken with backend
export async function loginWithGoogle(idToken) {
  if (!idToken) return { success: false, error: 'Missing idToken' };
  return safeApiCall(async () => {
    if (!csrfToken) await getCsrfToken();
    addBreadcrumb({ category: 'auth', message: 'Google login attempt', level: 'info' });
    const path = endpoints.auth.google || '/api/auth/google';
    const res = await fetch(url(path), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken || '',
      },
      credentials: 'include',
      body: JSON.stringify({ idToken }),
    });

    if (!res.ok) {
      const err = await safeJson(res);
      captureAuthError(new Error(err?.message || 'Google login failed'), 'google');
      throw new Error(err?.message || 'Google login failed');
    }

    const data = await res.json();
    if (data.user) localStorage.setItem('user', JSON.stringify(data.user));
    if (data.accessToken) {
      try { const { setAccessToken } = await import('./authClient'); setAccessToken(data.accessToken, data.expiresIn); } catch (e) {}
    }
    // Do not persist refreshToken in localStorage; server should set HttpOnly cookie

    addBreadcrumb({ category: 'auth', message: 'Google login success', level: 'info' });
    return { ...data, success: true };
  }, 'loginWithGoogle');
}
